<template>
  <fragment>
    <AppBody :subtitle="$t('people.users.eleven-admin')" :actions="actions">
      <CardModule untitle>
        <ExpansionPanelFilters>
          <v-form
            @submit.prevent="$pessoas_usuarios.filtroUsuarios()"
            class="search-forms"
          >
            <v-row>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  :label="$t('people.users.name')"
                  :placeholder="$t('people.users.insert-name')"
                  v-model="$pessoas_usuarios.filter.nome"
                  data-cy="nome"
                ></BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  label="E-mail"
                  :placeholder="$t('people.users.insert-email')"
                  v-model="$pessoas_usuarios.filter.email"
                  data-cy="email"
                ></BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseSelect
                  label="Status"
                  :placeholder="$t('people.users.select-status')"
                  :items="status"
                  data-cy="status"
                  item-text="text"
                  item-value="value"
                  v-model="$pessoas_usuarios.filter.status"
                  clearable
                  @click:clear-icon="clearFilterFields('status')"
                >
                </BaseSelect>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  :label="$t('people.users.initial-register-date')"
                  placeholder="dd/mm/aaaa"
                  v-model="$pessoas_usuarios.filter.dataInicio"
                  data-cy="data-inicial"
                  v-mask="'###/###/######'"
                  append-icon="mdi-calendar-blank-outline"
                ></BaseTextField>
              </v-col>
              <v-col cols="12" md="3" sm="4">
                <BaseTextField
                  :label="$t('people.users.final-register-date')"
                  placeholder="dd/mm/aaaa"
                  v-model="$pessoas_usuarios.filter.dataFim"
                  data-cy="data-final"
                  v-mask="'##/##/####'"
                  append-icon="mdi-calendar-blank-outline"
                ></BaseTextField>
              </v-col>
              <v-col data-cy="filtrar" class="d-flex justify-end">
                <BaseButton
                  variant="filter"
                  type="submit"
                  :loading="$pessoas_usuarios.usuarios_loading"
                >
                  {{ $t("registrations.filter") }}
                </BaseButton>
              </v-col>
            </v-row>
          </v-form>
        </ExpansionPanelFilters>

        <v-data-table
          class="table-body"
          item-key="idusuario"
          :no-data-text="$t('people.users.no-users-register-found')"
          :loading-text="$t('people.loading-info')"
          mobile-breakpoint="0"
          :loading="$pessoas_usuarios.usuarios_loading"
          :headers="headers"
          :items="filteredUsers"
          :items-per-page="10"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: $t('people.users.users-per-page'),
          }"
        >
          <template v-slot:item.ins_dthr="{ item }">
            {{ item.ins_dthr | dthr_pt_br }}
          </template>
          <template v-slot:item.actions="{ item }" class="table-actions">
            <div style="display: flex; justify-content: center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :color="$config.dados.corinputs"
                    height="30"
                    width="30"
                    small
                    icon
                    v-bind="attrs"
                    v-on="on"
                    @click="goToUser('editar', item)"
                    class="mr-2"
                  >
                    <v-icon>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("common.edit") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="item.status == 'ATIVO'"
                    :color="$config.dados.corinputs"
                    height="30"
                    v-bind="attrs"
                    v-on="on"
                    width="30"
                    small
                    icon
                    @click="
                      $pessoas_usuarios.desabilitarUsuario(item.idusuario)
                    "
                  >
                    <v-icon>mdi-account-cancel-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("people.disable") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-if="item.status == 'INATIVO'"
                    :color="$config.dados.corinputs"
                    height="30"
                    v-bind="attrs"
                    v-on="on"
                    width="30"
                    small
                    icon
                    @click="$pessoas_usuarios.habilitarUsuario(item.idusuario)"
                  >
                    <v-icon>mdi-account-check-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("people.enable") }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </CardModule>
    </AppBody>
  </fragment>
</template>

<script>
export default {
  name: "Usuarios",
  components: {
    AppBody: () => import("@/layouts/components/AppBody.vue"),
    CardModule: () => import("@/components/cards/CardModule.vue"),
    BaseTextField: () => import("@/components/forms/BaseTextField.vue"),
    BaseSelect: () => import("@/components/forms/BaseSelect.vue"),
    BaseButton: () => import("@/components/forms/BaseButton.vue"),
    ExpansionPanelFilters: () =>
      import("@/components/forms/ExpansionPanelFilters.vue"),
  },
  metaInfo() {
    const title = this.$t("people.users.eleven-admin");
    return {
      title: `${title} | ElevenTickets`,
      meta: [
        {
          property: "og:title",
          content: `${title} | ElevenTickets`,
        },
      ],
    };
  },
  computed: {
    actions() {
      return [
        {
          name: this.$t("people.users.new-eleven-admin"),
          action: () => this.goToUser("new", ""),
        },
      ];
    },
    status() {
      return [
        { text: this.$t("people.active"), value: "ATIVO" },
        { text: this.$t("people.inactive"), value: "INATIVO" },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("people.users.name"),
          align: "center",
          sortable: true,
          value: "nome",
        },
        {
          text: "E-mail",
          align: "center",
          sortable: true,
          value: "email",
        },
        {
          text: this.$t("people.users.register-date"),
          align: "center",
          sortable: true,
          value: "ins_dthr",
        },
        {
          text: "Status",
          align: "center",
          sortable: true,
          value: "status",
        },
        {
          text: this.$t("common.actions"),
          align: "center",
          sortable: false,
          fixed: false,
          value: "actions",
        },
      ];
    },
    filteredUsers() {
      return this.$pessoas_usuarios.usuarios;
    },
  },
  methods: {
    clearFilterFields(field) {
      switch (field) {
        case "status":
          this.$pessoas_usuarios.filter.status = "";
          break;
        default:
          break;
      }
    },
    goToUser(type, data) {
      this.$pessoas_usuarios.usuarioAddEdit = type;
      if (type === "editar") {
        this.$router.push({
          name: "userSettings",
          params: {
            type: "editar",
          },
        });
        this.$pessoas_usuarios.cadastro.nome = data.nome;
        this.$pessoas_usuarios.cadastro.avatar.imagem = data.avatar;
        this.$pessoas_usuarios.cadastro.email = data.email;
        this.$pessoas_usuarios.cadastro.senha = data.senha;
        this.$pessoas_usuarios.cadastro.urlHome = data.urlHome;
        this.$pessoas_usuarios.cadastro.status = data.status;
        this.$pessoas_usuarios.cadastro.iduser = data.idusuario;
        return;
      }
      this.$router.push({
        name: "userSettings",
        params: {
          type: "cadastrar",
        },
      });
    },
  },
  mounted() {
    this.$pessoas_usuarios.getUsuarios();
  },
  destroyed() {
    this.$pessoas_usuarios.limpaFilterDeUsuario();
  },
};
</script>

<style scoped>
.pessoas-usuarios-button3 {
  color: var(--dl-color-gray-white);
  width: 30px;
  height: 30px;
  display: flex;
  padding: 0;
  align-items: center;
  border-width: 0;
  margin-right: 0.625rem;
  flex-direction: row;
  justify-content: center;
  background-color: var(--dl-color-gray-black);
}
</style>
